import type React from "react";
import { useRef, useState, useEffect, useCallback, type FC, useImperativeHandle } from "react";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/flexkit/Command";
import { getCaretCoordinates, getCurrentWord, replaceWord } from "@/components/ai/input-with-code-context/utils";
import { Textarea, type TextareaProps } from "@/components/podkit/forms/Textarea";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PlainProject } from "@/queries/project-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { EnvironmentChanges } from "@/routes/environments/SidebarEnvironmentList";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import type { EnvironmentsGroup } from "@/hooks/use-environments-by-projects";
import { useBranch } from "@/hooks/use-branch";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainAgent } from "@/queries/agent-queries";

export type Context = CodeContext | AgentContext;
export type CodeContext = { type: "project"; value: PlainProject } | { type: "environment"; value: PlainEnvironment };
export type AgentContext = { type: "agent"; value: PlainAgent };

export type InputWithCodeContextImperativeHandle = {
    triggerContextCompletion: () => void;
};

type InputWithCodeContextProps = {
    imperativeHandleRef: React.RefObject<InputWithCodeContextImperativeHandle>;
    textValue: string;
    setTextValue: React.Dispatch<React.SetStateAction<string>>;
    projects: PlainProject[];
    environmentGroups: EnvironmentsGroup[];
    agents: PlainAgent[];
    onCodeContextSelect: (context: CodeContext) => void;
    onAgentContextSelect: (context: AgentContext) => void;
} & Omit<TextareaProps, "value" | "onChange"> &
    PropsWithClassName;

export const InputWithCodeContext: FC<InputWithCodeContextProps> = ({
    imperativeHandleRef,
    textValue,
    setTextValue,
    onCodeContextSelect,
    onAgentContextSelect,
    agents,
    projects,
    environmentGroups,
    className,
    ...props
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const dropdownInputRef = useRef<HTMLInputElement>(null);
    const [commandValue, setCommandValue] = useState("");

    const handleBlur = useCallback((_e: Event) => {
        const dropdown = dropdownRef.current;
        if (dropdown) {
            dropdown.classList.add("hidden");
            setCommandValue("");
        }
    }, []);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const textarea = textareaRef.current;
            const input = dropdownInputRef.current;
            const dropdown = dropdownRef.current;
            if (textarea && input && dropdown) {
                const currentWord = getCurrentWord(textarea);
                const isDropdownHidden = dropdown.classList.contains("hidden");
                if (currentWord.startsWith("@") && !isDropdownHidden) {
                    if (e.key === "Escape" || e.keyCode === 27) {
                        setCommandValue("");
                        dropdown.classList.add("hidden");
                        return;
                    }

                    if (
                        e.key === "ArrowUp" ||
                        e.keyCode === 38 ||
                        e.key === "ArrowDown" ||
                        e.keyCode === 40 ||
                        e.key === "Enter" ||
                        e.keyCode === 13
                    ) {
                        e.preventDefault();
                        input.dispatchEvent(new KeyboardEvent("keydown", e));
                    }
                }
            }
        },
        [textareaRef],
    );

    const onTextValueChange = useCallback(
        (text: string) => {
            const textarea = textareaRef.current;
            const dropdown = dropdownRef.current;
            const dropdownList = dropdownListRef.current;
            if (textarea && dropdown && dropdownList) {
                const caret = getCaretCoordinates(textarea, textarea.selectionEnd);
                const currentWord = getCurrentWord(textarea);
                // TODO(mads): Find a way to not hardcode this to the width of the dropdown
                // We can't use dropdown.clientWidth because the element is hidden at this point
                const dropdownWidth = 300;
                const dropdownMaxHeight = 300;
                setTextValue(text);
                if (currentWord.startsWith("@")) {
                    setCommandValue(currentWord.slice(1));
                    let offsetLeft = textarea.offsetLeft + caret.left;
                    if (offsetLeft + dropdownWidth > window.innerWidth) {
                        // If the dropdown would go past the window width, we move it to the left of the caret instead.
                        offsetLeft = offsetLeft - dropdownWidth;
                    }
                    dropdown.style.left = offsetLeft + "px";
                    dropdown.style.top = textarea.offsetTop + caret.top + caret.height + "px";

                    // If the dropdown would go past the window height, we limit it to the available space that's left
                    const maxHeight = window.innerHeight - (textarea.offsetTop + caret.top + caret.height) - 15;
                    dropdownList.style.maxHeight = Math.min(maxHeight, dropdownMaxHeight) + "px";

                    dropdown.classList.remove("hidden");
                    dropdownList.scrollTop = 0;
                } else {
                    // REMINDER: apparently, we need it when deleting
                    if (commandValue !== "") {
                        setCommandValue("");
                    }
                    dropdown.classList.add("hidden");
                }
            }
        },
        [textareaRef, setTextValue, commandValue],
    );

    const onCommandSelect = useCallback(
        (context: Context) => {
            const textarea = textareaRef.current;
            const dropdown = dropdownRef.current;
            if (textarea && dropdown) {
                replaceWord(textarea, "");
                setCommandValue("");
                dropdown.classList.add("hidden");

                if (context.type === "agent") {
                    onAgentContextSelect(context);
                } else {
                    onCodeContextSelect(context);
                }
            }
        },
        [onAgentContextSelect, onCodeContextSelect],
    );

    const handleMouseDown = useCallback((e: Event) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleSectionChange = useCallback(
        (_e: Event) => {
            const textarea = textareaRef.current;
            const dropdown = dropdownRef.current;
            if (textarea && dropdown) {
                const currentWord = getCurrentWord(textarea);
                if (!currentWord.startsWith("@") && commandValue !== "") {
                    setCommandValue("");
                    dropdown.classList.add("hidden");
                }
            }
        },
        [commandValue, textareaRef],
    );

    /**
     * This allows the parent component to programatically trigger context completions
     */
    useImperativeHandle(
        imperativeHandleRef,
        () => {
            return {
                triggerContextCompletion() {
                    if (textareaRef.current) {
                        textareaRef.current.focus();
                        if (!textareaRef.current.value.endsWith(" ")) {
                            textareaRef.current.value += " ";
                        }
                        textareaRef.current.value += "@";
                        onTextValueChange(textareaRef.current.value);
                    }
                },
            };
        },
        [onTextValueChange],
    );

    useEffect(() => {
        const textarea = textareaRef.current;
        const dropdown = dropdownRef.current;
        textarea?.addEventListener("keydown", handleKeyDown);
        textarea?.addEventListener("blur", handleBlur);
        document?.addEventListener("selectionchange", handleSectionChange);
        dropdown?.addEventListener("mousedown", handleMouseDown);
        return () => {
            textarea?.removeEventListener("keydown", handleKeyDown);
            textarea?.removeEventListener("blur", handleBlur);
            document?.removeEventListener("selectionchange", handleSectionChange);
            dropdown?.removeEventListener("mousedown", handleMouseDown);
        };
    }, [handleBlur, handleKeyDown, handleMouseDown, handleSectionChange, textareaRef]);

    return (
        <div className="w-full">
            <Textarea
                className={cn(className)}
                ref={textareaRef}
                autoComplete="off"
                autoCorrect="off"
                value={textValue}
                onChange={(e) => onTextValueChange(e.target.value)}
                rows={5}
                {...props}
            />
            <Command ref={dropdownRef} className={cn("absolute hidden h-auto w-[300px]")}>
                {/* We're controlling the command search input programmatically, so we don't want to show it in the UI*/}
                <div className="hidden">
                    {/* REMINDER: className="hidden" won't hide the SearchIcon and border */}
                    <CommandInput ref={dropdownInputRef} value={commandValue} />
                </div>
                <CommandList ref={dropdownListRef} className="max-h-[300px]">
                    <CommandEmpty>No results found</CommandEmpty>
                    <CommandGroup heading="Agents">
                        {agents.map((a) => {
                            return (
                                <CommandItem key={a.id} onSelect={() => onCommandSelect({ type: "agent", value: a })}>
                                    <Text className="text-sm text-content-primary">{a.metadata?.name || a.id}</Text>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                    <CommandGroup heading="Projects">
                        {projects.map((p, idx) => {
                            return (
                                <CommandItem
                                    key={p.id}
                                    data-testid={`code-context-project-${p.id}`}
                                    onSelect={(_v) => onCommandSelect({ type: "project", value: p })}
                                    // We don't have a uniqueness constraint on the name of projects, so we're adding -{idx} to achieve that
                                    // Otherwise Command gets confused when two values have the same "value".
                                    // We're using the array index rather than the project id to reduce the noise in the search results.
                                    value={`${p.metadata?.name}-${idx}`}
                                    className="h-8"
                                >
                                    <Text className="text-sm text-content-primary">{p.metadata?.name || p.id}</Text>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                    {environmentGroups.map((group) => {
                        const groupName = group.project?.metadata?.name || group.repoFullName || "No project or repo";
                        return (
                            <>
                                <CommandSeparator />
                                <CommandGroup heading={`Environments: ${groupName}`}>
                                    {group.environments.map((env) => (
                                        <CommandItemEnvironment
                                            key={env.id}
                                            groupName={groupName}
                                            env={env}
                                            onCommandSelect={onCommandSelect}
                                        />
                                    ))}
                                </CommandGroup>
                            </>
                        );
                    })}
                </CommandList>
            </Command>
        </div>
    );
};

const CommandItemEnvironment: FC<{
    groupName: string;
    env: PlainEnvironment;
    onCommandSelect: (context: CodeContext) => void;
}> = ({ groupName, env, onCommandSelect }) => {
    const environmentName = useBranch(env);
    return (
        <CommandItem
            key={env.id}
            data-testid={`code-context-environment-${env.id}`}
            onSelect={(_v) => onCommandSelect({ type: "environment", value: env })}
            value={`${groupName}-${environmentName}-${env.id}`}
            className="h-8"
        >
            <div className="flex w-full min-w-0 items-center gap-1">
                <EnvironmentStatusDot className="m-0 shrink-0" size="lg" env={env} />
                <Text className="min-w-6 grow truncate text-sm text-content-primary">{environmentName}</Text>
                <EnvironmentChanges git={env.status?.content?.git} className="text-[10px]" />
            </div>
        </CommandItem>
    );
};
